import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, Smartphone, Check, X, Info, Loader2, Send, ShoppingCart, Truck, Factory, Boxes, CreditCard, ClipboardList, ShieldCheck } from 'lucide-react';
import { enablePush, disablePush, resubscribePush, currentPushPermission, fcmConfigured, isIOS, isStandalonePWA } from '../lib/push';
import { GoogleAccountCard } from '../components/google/GoogleAccountCard';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MODULE_ICON = {
  sales: ShoppingCart, dispatch: Truck, production: Factory,
  inward: Boxes, payments: CreditCard, samples: ClipboardList, compliance: ShieldCheck,
};

export default function NotificationPreferences() {
  const [catalog, setCatalog] = useState([]);
  const [prefs, setPrefs] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pushPerm, setPushPerm] = useState(currentPushPermission());
  const [enabling, setEnabling] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [c, p] = await Promise.all([
          axios.get(`${API}/push/events-catalog`, { withCredentials: true }),
          axios.get(`${API}/push/preferences`, { withCredentials: true }),
        ]);
        setCatalog(c.data || []);
        setPrefs(p.data?.prefs || {});
      } catch (e) { toast.error('Could not load preferences'); }
      finally { setLoading(false); }
    })();
  }, []);

  const toggle = (key) => setPrefs(p => ({ ...p, [key]: !p[key] }));

  const toggleModule = (events, enable) => {
    setPrefs(p => {
      const next = { ...p };
      events.forEach(ev => { next[ev.key] = enable; });
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/push/preferences`, { prefs }, { withCredentials: true });
      toast.success('Preferences saved');
    } catch (e) { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const handleEnable = async () => {
    setEnabling(true);
    try {
      await enablePush();
      setPushPerm('granted');
      toast.success('Push notifications enabled');
    } catch (e) {
      toast.error(e.message === 'Permission denied'
        ? 'You denied notifications. Enable them in your browser settings for this site.'
        : (e.message || 'Could not enable push'));
    } finally { setEnabling(false); }
  };

  const handleDisable = async () => {
    await disablePush();
    toast('Push disabled on this device');
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const res = await axios.post(`${API}/push/test`, {}, { withCredentials: true });
      toast.success(`Test sent to ${res.data.devices_reached} device(s)`);
    } catch { toast.error('Test send failed'); }
    finally { setTesting(false); }
  };

  if (loading) return (
    <div className="min-h-[50vh] flex items-center justify-center text-zinc-500 text-sm">
      <Loader2 className="animate-spin mr-2" size={14} /> Loading preferences…
    </div>
  );

  const iosNotInstalled = isIOS() && !isStandalonePWA();

  return (
    <div className="max-w-4xl mx-auto space-y-5 pb-10">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl font-semibold text-zinc-50">Notification Preferences</h1>
        <p className="text-zinc-500 text-sm mt-1">Pick the events you want pushed to your phone / desktop — like WhatsApp.</p>
      </div>

      {/* Daily Briefing opt-in */}
      <BriefingToggle />

      {/* Google Calendar & Tasks (TI reminders) */}
      <GoogleAccountCard />

      {/* Push status card */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5" data-testid="push-status-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${pushPerm === 'granted' ? 'bg-green-500/15 text-green-400' : 'bg-amber-500/15 text-amber-400'}`}>
              <Bell size={18} />
            </div>
            <div>
              <div className="text-zinc-50 text-sm font-semibold">
                Push to this device —
                {pushPerm === 'granted' && <span className="text-green-400 ml-1">Enabled</span>}
                {pushPerm === 'denied' && <span className="text-red-400 ml-1">Blocked by browser</span>}
                {pushPerm === 'default' && <span className="text-amber-300 ml-1">Not enabled yet</span>}
                {pushPerm === 'unsupported' && <span className="text-zinc-400 ml-1">Unsupported browser</span>}
              </div>
              <div className="text-zinc-500 text-xs mt-1">
                {pushPerm === 'granted'
                  ? 'You will receive push alerts even when the app tab is closed.'
                  : 'Tap Enable. We\'ll register this device so Tirth ERP can push alerts to you.'}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {pushPerm !== 'granted' && (
              <button data-testid="enable-push-btn" disabled={enabling || !fcmConfigured}
                onClick={handleEnable}
                className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-black rounded-xl font-semibold text-sm flex items-center gap-2 disabled:opacity-50">
                {enabling && <Loader2 size={14} className="animate-spin" />}
                Enable Push
              </button>
            )}
            {pushPerm === 'granted' && (
              <>
                <button data-testid="test-push-btn" disabled={testing} onClick={sendTest}
                  className="px-3 py-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-100 rounded-xl text-sm flex items-center gap-2">
                  {testing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  Send Test
                </button>
                <button onClick={handleDisable}
                  className="px-3 py-2 bg-zinc-900 hover:bg-red-900/30 border border-zinc-700 hover:border-red-800 text-zinc-300 hover:text-red-300 rounded-xl text-sm">
                  Disable
                </button>
              </>
            )}
          </div>
        </div>
        {iosNotInstalled && (
          <div className="mt-4 p-3 rounded-xl bg-blue-500/10 border border-blue-500/30 flex gap-2" data-testid="ios-install-hint">
            <Smartphone size={14} className="text-blue-300 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-blue-200 leading-relaxed">
              <div className="font-semibold">iPhone users — install the app first</div>
              Safari → tap the Share icon <span className="font-mono">⎋</span> → <b>Add to Home Screen</b>. Then open Tirth ERP from your home screen and tap Enable Push above.
            </div>
          </div>
        )}
        {!fcmConfigured && (
          <div className="mt-3 text-xs text-zinc-500 flex items-center gap-1.5">
            <Info size={11} /> Server-side Firebase is not yet configured. Ask admin.
          </div>
        )}
        {/* Diagnostics — surface raw subscription state so the user can
            confirm OTHER recipients (e.g. manager@) are actually subscribed
            on their own device, not just see "I clicked Enable" on this one. */}
        <Diagnostics />
      </div>

      {/* Events catalog — one card per module */}
      {catalog.map(group => {
        const events = group.events;
        const enabledCount = events.filter(ev => prefs[ev.key]).length;
        const allOn = enabledCount === events.length;
        const noneOn = enabledCount === 0;
        const Icon = MODULE_ICON[group.module] || Bell;
        return (
          <div key={group.module} className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden" data-testid={`pref-group-${group.module}`}>
            <div className="px-5 py-3 border-b border-zinc-800 bg-zinc-950/50 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-amber-500/15 text-amber-400 flex items-center justify-center">
                  <Icon size={14} />
                </div>
                <div>
                  <div className="text-zinc-50 text-sm font-semibold">{group.label}</div>
                  <div className="text-zinc-500 text-[10px]">{enabledCount}/{events.length} events on · requires <span className="font-mono">{group.permission}</span> permission</div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button data-testid={`select-all-${group.module}`} onClick={() => toggleModule(events, true)} disabled={allOn}
                  className="text-[11px] text-green-400 hover:text-green-300 px-2 py-1 rounded hover:bg-zinc-800 disabled:opacity-40">
                  Select all
                </button>
                <button data-testid={`select-none-${group.module}`} onClick={() => toggleModule(events, false)} disabled={noneOn}
                  className="text-[11px] text-red-300 hover:text-red-200 px-2 py-1 rounded hover:bg-zinc-800 disabled:opacity-40">
                  Select none
                </button>
              </div>
            </div>
            <div className="p-2">
              {events.map(ev => {
                const on = !!prefs[ev.key];
                return (
                  <label key={ev.key}
                    data-testid={`pref-${ev.key}`}
                    className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl hover:bg-zinc-800/40 cursor-pointer transition-colors">
                    <div className="text-zinc-200 text-sm">{ev.label}</div>
                    {/* Toggle */}
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); toggle(ev.key); }}
                      aria-pressed={on}
                      className={`relative w-10 h-5.5 rounded-full transition-colors ${on ? 'bg-amber-500' : 'bg-zinc-700'}`}
                      style={{ width: '40px', height: '22px' }}
                    >
                      <span className={`absolute top-0.5 left-0.5 w-[18px] h-[18px] rounded-full bg-white shadow transition-transform ${on ? 'translate-x-[18px]' : ''}`} />
                    </button>
                  </label>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Save button */}
      <div className="sticky bottom-4 z-10 flex justify-end">
        <button data-testid="save-prefs-btn" disabled={saving} onClick={save}
          className="px-6 py-3 bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-xl shadow-2xl flex items-center gap-2 disabled:opacity-50">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
          Save Preferences
        </button>
      </div>
    </div>
  );
}

function BriefingToggle() {
  const [loading, setLoading] = React.useState(true);
  const [enabled, setEnabled] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    axios.get(`${API}/push/briefing-pref`, { withCredentials: true })
      .then(r => setEnabled(!!r.data?.enabled))
      .finally(() => setLoading(false));
  }, []);

  const toggle = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/push/briefing-pref`, { enabled: !enabled }, { withCredentials: true });
      setEnabled(!!res.data.enabled);
      toast.success(res.data.enabled ? 'Daily briefing email: ON' : 'Daily briefing email: OFF');
    } catch { toast.error('Failed to update'); }
    finally { setSaving(false); }
  };

  const testBriefing = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/portal/admin/send-briefing`, {}, { withCredentials: true });
      if (res.data?.last_run) {
        toast.success(`Test brief sent · ${res.data.last_run.emails_sent} email(s) · ${res.data.last_run.counts?.order_today ?? 0} materials to order today`);
      } else {
        toast.success('Test briefing sent');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send test'); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-zinc-900 border border-amber-500/30 rounded-2xl p-5" data-testid="briefing-toggle-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-500/15 text-amber-400 flex items-center justify-center">
            <Bell size={18} />
          </div>
          <div>
            <div className="text-zinc-50 text-sm font-semibold">Daily Brief Email · 10 AM IST</div>
            <div className="text-zinc-500 text-xs mt-0.5 max-w-md leading-relaxed">
              One email at <b>10 AM IST</b> covering the last 24 h: inward (materials received), production (finished goods made, kg),
              sales &amp; dispatch, transformations, receipts &amp; payments pending/due, and materials to order with demand and
              procurement timeline (order-by / needed-by dates, suggested qty, vendor).
              <a href={`${API}/briefing/preview.html`} target="_blank" rel="noreferrer" className="text-amber-400 hover:underline ml-1" data-testid="briefing-preview-link">Preview today's brief ↗</a>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={testBriefing} disabled={saving || loading} data-testid="briefing-test-btn"
            className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg text-xs font-semibold disabled:opacity-50">
            Send test now
          </button>
          <button onClick={toggle} disabled={saving || loading} data-testid="briefing-toggle-btn"
            className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all ${enabled ? 'bg-amber-500 text-black hover:bg-amber-400' : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'} disabled:opacity-50`}>
            {loading ? '…' : (enabled ? 'Enabled · click to disable' : 'Enable')}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Diagnostics — surfaces who's actually subscribed on which device.
 * Helps users debug: "I enabled push on phone A but not getting alerts when
 * the order is created on phone B." Now they can tell at a glance whether
 * the OTHER user has a registered FCM token at all.
 */
function Diagnostics() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resubBusy, setResubBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/push/diagnostics`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Diagnostics failed');
    } finally {
      setLoading(false);
    }
  };

  // Force a fresh FCM token registration. Use when the stored token was
  // issued against a previous Firebase project — common cause of "0 devices
  // delivered" with INVALID_ARGUMENT errors in the backend log.
  const resubscribe = async () => {
    setResubBusy(true);
    try {
      const tok = await resubscribePush();
      if (tok) {
        toast.success('Re-subscribed successfully — try the test push again');
        await load();
      } else {
        toast.error('Re-subscription failed — check browser permissions');
      }
    } catch (e) {
      toast.error('Re-subscribe failed: ' + (e.message || 'unknown'));
    } finally {
      setResubBusy(false);
    }
  };

  return (
    <div className="mt-4 border-t border-zinc-800 pt-3">
      <button onClick={() => { setOpen(o => !o); if (!data) load(); }}
        data-testid="push-diag-toggle"
        className="text-xs text-zinc-400 hover:text-amber-300 flex items-center gap-1.5">
        <Info size={11} /> {open ? 'Hide diagnostics' : 'Show subscriber diagnostics'}
      </button>
      {open && (
        <div className="mt-3 space-y-3 bg-zinc-950/60 border border-zinc-800 rounded-xl p-3" data-testid="push-diag-panel">
          {loading && <div className="text-zinc-500 text-xs flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Loading…</div>}
          {data && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Stat k="FCM Admin" v={data.fcm_admin_ready ? 'Ready' : 'Not loaded'} ok={data.fcm_admin_ready} />
                <Stat k="Total subscribers" v={data.total_subscribers} ok={data.total_subscribers > 0} />
                <Stat k="Users with tokens" v={data.total_users_with_tokens} ok={data.total_users_with_tokens > 0} />
                <Stat k="Your devices" v={data.my_tokens?.length || 0} ok={(data.my_tokens?.length || 0) > 0} />
              </div>
              {/* Re-subscribe rescue button — drops old token, requests fresh
                  one from current Firebase project. The fix-it-yourself path
                  when "0 devices delivered" is caused by stale tokens. */}
              <div className="flex flex-wrap gap-2 items-center">
                <button onClick={resubscribe} disabled={resubBusy}
                  data-testid="push-resubscribe-btn"
                  className="text-[11px] bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 border border-amber-500/40 px-3 py-1.5 rounded-lg disabled:opacity-50 flex items-center gap-1.5">
                  {resubBusy ? <Loader2 size={11} className="animate-spin" /> : <Bell size={11} />}
                  Force fresh token (re-subscribe)
                </button>
                <span className="text-[10px] text-zinc-500">Use this if test pushes show "0 devices delivered"</span>
              </div>
              {data.my_tokens?.length > 0 && (
                <div>
                  <div className="text-zinc-400 text-[11px] font-semibold mb-1">My devices</div>
                  <div className="space-y-1">
                    {data.my_tokens.map((t, i) => (
                      <div key={i} className="text-[10px] text-zinc-500 font-mono bg-zinc-900 rounded px-2 py-1.5 border border-zinc-800">
                        <div>{t.token_preview} · {t.platform}</div>
                        <div className="text-zinc-600">{(t.user_agent || '').slice(0, 80)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <div className="text-zinc-400 text-[11px] font-semibold mb-1">All subscribers (per user)</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  {(data.subscribers_by_user || []).map((u, i) => (
                    <div key={i} className="text-[11px] text-zinc-300 bg-zinc-900 rounded px-2 py-1 border border-zinc-800 flex justify-between">
                      <span>{u.user_name || u.user_id || '—'}</span>
                      <span className="text-amber-400 font-mono">{u.count} device{u.count > 1 ? 's' : ''}</span>
                    </div>
                  ))}
                </div>
                {(data.subscribers_by_user || []).length === 0 && (
                  <div className="text-amber-400 text-[10px]">No users have subscribed yet — open this app on each phone/desktop you want to receive pushes on, and click "Enable Push".</div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ k, v, ok }) {
  return (
    <div className="bg-zinc-900 rounded-lg px-2 py-1.5 border border-zinc-800">
      <div className="text-[9px] uppercase text-zinc-500">{k}</div>
      <div className={`text-xs font-semibold ${ok ? 'text-green-400' : 'text-amber-400'}`}>{v}</div>
    </div>
  );
}


